import React, { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { enumUtil } from '../../../utils/Enum';
import { ServerRequestProtectedWithoutToken, ServerRequestPublic } from '../../../utils/ServerDataRequest';
import NotificationHandler from '../../../utils/NotificationHandler';
import Loader from '../../shared/Loader/Loader';
import FormatPhoneNumber from '../../../utils/FormatPhoneNumber';
import { formatFax } from '../../../utils/Formatting';
import SignEncounter from '../PatientDetails/Encounter/Components/EncounterRightPortion/TemplateView/Components/SignEncounter';
import FormValidator from '../../../utils/FormValidator';
import isNull from '../../../utils/NullChecking';

const PatientSigntureForm = () => {

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search)
  const ID = queryParams.get('id')
  const [isSign, setIsSign] = useState(false);
  const [imgFile, setImgFile] = useState(null);
  const [isDefaultSignature, setIsDefaultSignature] = useState(false);
  const [isConsentChecked, setIsConsentChecked] = useState(false);
  const [otpValue, setOtpValue] = useState('');
  const [otpValidationError, setOtpValidationError] = useState('');
  const [isAlreadySubmitted, setIsAlreadySubmitted] = useState(false);

  const API_URL = enumUtil.controller.EHRTemplate; // same endpoint

  const API_URLSave = enumUtil.controller.PATIENT; // same endpoint

  const [isLoading, setIsLoading] = useState(false);
  const [practiceData, setPracticeData] = useState({});
  const [formData, setFormData] = useState({
    TemplateName: "",
    TemplateType: "",
    OTP: "",
    GuidID: ID,
    PatientName: "",
    Status: ""
  });
  const [isFormSubmitted, setIsFormSubmitted] = useState(false);

  const LoadData = (id) => {
    setIsLoading(true);

    ServerRequestProtectedWithoutToken(API_URL + "/FindByGUIDID", 'post', {
      "TableName": enumUtil.enumAPITableName.Patient,
      "Value": id
    }).then((response) => {
      let { Data, EHRTemplateID, PracticeData, ...formData } = response;
      setFormData(prev => ({ ...prev, ...formData }));
      PracticeData && setPracticeData({ ...PracticeData[0] });
      if (response.Status === "Submitted") {
        setIsFormSubmitted(true);
      }
      setIsLoading(false);
    }).catch((error) => {
      NotificationHandler("This form is no longer available.", enumUtil.enumtoaster.error);
      setIsLoading(false);
    });
  };

  useEffect(() => {
    if (ID) {
      setFormData(prev => ({ ...prev, GuidID: ID }));
      LoadData(ID);
    }
  }, [ID]);

  const handleSignatureSave = (_, filename, base64, options = {}) => {
    const imageSrc = base64.startsWith('data:') ? base64 : `data:image/png;base64,${base64}`;
    setImgFile(imageSrc);
    setIsSign(false);
    setIsDefaultSignature(options.isDefaultSignature || false);
  };

  const handleSubmit = () => {
    const errors = validateForm();
    const hasError = Object.values(errors).some(msg => msg !== '');
    if (hasError) {
      NotificationHandler('Please fill all required fields.', enumUtil.enumtoaster.error);
      return;
    }

    const otpError = validateOTP();
    if (otpError) {
      NotificationHandler('OTP is required.', enumUtil.enumtoaster.error);
      return;
    }

    submitFormData();
  };

  const submitFormData = () => {
    setIsLoading(true);

    let payload;
    if (formData?.IsDefaultSignature) {
      payload = {
        GuidID: ID,
        SignaturePath: formData?.SignaturePath,
        Otp: otpValue,
        IsDefaultSignature: formData?.IsDefaultSignature,
        SignatureBase64: '',
      };
    } else {
      payload = {
        GuidID: ID,
        SignatureBase64: imgFile,
        Otp: otpValue,
        IsDefaultSignature: isDefaultSignature,
        SignaturePath: '',
      };
    }

    ServerRequestProtectedWithoutToken(
      API_URLSave + "/confirm-signature", 'post', payload)
      .then((response) => {
        setIsFormSubmitted(true);
        NotificationHandler('Form submitted successfully!', enumUtil.enumtoaster.success);
      })
      .catch((error) => {
        setIsLoading(false);
        NotificationHandler(error.response.data.message, enumUtil.enumtoaster.error);
      })
      .finally(() => setIsLoading(false));
  };

  const ValidationModal = {
    SignaturePath: "",
    OTP: "",
    ConsentCheckbox: "",
  };

  const [validationErrors, setValidationErrors] = useState(ValidationModal);

  const validateForm = () => {
    const errors = {
      SignaturePath: FormValidator(imgFile, enumUtil.enumValidationType.Required, 'Signature'),
      OTP: FormValidator(otpValue, enumUtil.enumValidationType.Required, 'OTP'),
      ConsentCheckbox: !isConsentChecked ? 'You must consent to the electronic signature.' : '',
    };
    setValidationErrors(errors);
    return errors;
  };

  const handleRemoveFile = () => {
    setImgFile(null);
  };

  const validateOTP = () => {
    const error = FormValidator(otpValue, enumUtil.enumValidationType.Required, 'OTP');
    setOtpValidationError(error);
    return error;
  };

  const handleClearForm = () => {
    setImgFile(null);
    setIsSign(false);
    setIsConsentChecked(false);
    setOtpValue('');
    setValidationErrors(ValidationModal);
    setOtpValidationError('');
    setIsDefaultSignature(false);
    setFormData(prev => ({ ...prev, IsDefaultSignature: false, SignaturePath: '' }));
  }


  const loadSignatureFromServer = async (path) => {
    setIsLoading(true);
    try {
      const response = await ServerRequestPublic(
        enumUtil.controller.Wasabi + "/DownloadFile",
        'post',
        { BucketPath: path },
        "Download"
      );
      if (response.type === "application/png" || response.type === "application/jpg" || response.type === "application/jpeg") {
        const blob = response;
        const objectURL = URL.createObjectURL(blob);
        setImgFile(objectURL);
      } else {
        NotificationHandler("Unsupported signature format", enumUtil.enumtoaster.error);
      }
    } catch (error) {
      NotificationHandler("Failed to load signature", enumUtil.enumtoaster.error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (formData?.IsDefaultSignature && formData?.SignaturePath) {
      loadSignatureFromServer(formData.SignaturePath);
    }
  }, [formData?.IsDefaultSignature, formData?.SignaturePath]);

  const handleRemoveFileDefault = () => {
    setImgFile(null);
    setFormData(prev => ({ ...prev, SignaturePath: '', IsDefaultSignature: false }));
  };



  return (
    <div className='ar_pat-info-form-fill d-flex justify-content-center'>
      {isLoading ? <Loader /> : null}
      {isFormSubmitted || formData?.SignatureFormStatus === 'Submitted' ?
        <div className="ar_fill-content-box d-flex flex-column">
          <div className="d-flex align-items-center justify-content-between bg-white br-bottom-1 za_br_light pb-1-5 practice-info">
            <div className="">
              <img src={practiceData?.img} alt="" style={{ maxHeight: '55px' }} />
            </div>
            <div className="text-center">
              <p className='heading text-black fs-16'>{practiceData?.PracticeName}</p>

              <p className="text text-black fs-14"> {practiceData?.PracticeAddress} <br />
                {practiceData?.PracticeCity} ,  {practiceData?.PracticeState},  {practiceData?.PracticeZipCode}</p>

            </div>
            <div className="d-flex gap-10">
              <div className="">
                <p className='text text-black font-m fs-14 pb-2px'>Tel:</p>
                <p className='text text-black font-m fs-14'>Fax:</p>
              </div>
              <div className="">
                <p className='text text-black  pb-2px fs-14'>{FormatPhoneNumber(practiceData?.Phone)}</p>
                <p className='text text-black fs-14'>{formatFax(practiceData?.fax)}</p>
              </div>
            </div>
          </div>

          <div className="bg-white">
            <p className="heading fs-16 lh-17 text-white form-heading">Patient’s Signature</p>
          </div>

          <div className='sucsses_meessage d-flex align-items-center gap-20'>
            <div>
              <i className='icon-tick-mark fs-49'></i>
            </div>
            <div >
              <p className='fs-22 text font-m green_Color'>
                {isFormSubmitted ? 'Form Submission Successful!' : 'This form is already submitted!'}
              </p>
              <p className='text fs-14'>Thank you for submitting the form. If you need further assistance, feel free to contact us.</p>
            </div>
          </div>
        </div>
        :
        <div className="ar_fill-content-box d-flex flex-column gap-16">
          <div className="d-flex align-items-center justify-content-between bg-white br-bottom-1 za_br_light pb-1-5 practice-info">
            <div className="">
              <img src={practiceData?.img} alt="" style={{ maxHeight: '55px' }} />
            </div>
            <div className="text-center">
              <p className='heading text-black fs-16'>{practiceData?.PracticeName}</p>

              <p className="text text-black fs-14"> {practiceData?.PracticeAddress} <br />
                {practiceData?.PracticeCity} ,  {practiceData?.PracticeState},  {practiceData?.PracticeZipCode}
              </p>

            </div>
            <div className="d-flex gap-10">
              <div className="">
                <p className='text text-black font-m fs-14 pb-2px'>Tel:</p>
                <p className='text text-black font-m fs-14'>Fax:</p>
              </div>
              <div className="">
                <p className='text text-black  pb-2px fs-14'>{FormatPhoneNumber(practiceData?.Phone)}</p>
                <p className='text text-black fs-14'>{formatFax(practiceData?.fax)}</p>
              </div>
            </div>
          </div>

          <div className="bg-white">
            <p className="heading fs-16 lh-17 text-white form-heading">Patient’s Signature</p>
            <p className="text fs-12 lh-14 form-instruction p-2">Please fill out all fields as accurately as possible. This information helps us provide you with the best care. If you need any assistance with completing this form, please don't hesitate to contact us.</p>
          </div>

          <div className="ar_star-form-Preview">
            <div className="Form-content d-flex flex-column gap-16 pe-1 hover-scrollbar ar-Roi-fill-form h-100 overflow-visible">

              <div className={`content-box d-flex gap-10  ${imgFile ? 'overflow-hidden' : ''} `}>
                <p className={`text fs-14 text-black min-w-156px d-flex justify-content-end gap-01 ${imgFile ? '' : 'align-items-center'} `}>
                  Patient Signature<span className="text font-m Denied-clr fs-14">*</span>
                </p>

                {formData?.IsDefaultSignature ?
                  <div className="position-relative sign-here-box provider-sign-box cursor-pointer flex-center h-100 px-5 py-3">
                    <div className="flex gap-2.5 align-items-center">
                      <div className="">
                        <img
                          src={imgFile || ''}
                          alt="Patient Signature"
                          style={{ maxHeight: '81px' }}
                        />
                        <span className="cross-icon flex-center" onClick={handleRemoveFileDefault}>
                          <i className="icon-x-cros fs-14 lh-14 text-black"></i>
                        </span>
                      </div>
                    </div>
                  </div>
                  : <>
                    {!imgFile ?
                      <div className={`input w-100 ${validationErrors.SignaturePath ? 'error' : ''}`}>
                        <div className="form-control cursor-pointer" onClick={() => setIsSign(true)}>
                          <p className="text color-7070 select-none position-absolute translate-middle-y top-50">Click to sign here</p>
                        </div>
                        {validationErrors.SignaturePath && (<>{validationErrors.SignaturePath}</>)}
                      </div>
                      :
                      <div className="position-relative sign-here-box provider-sign-box cursor-pointer flex-center h-100 px-5 py-3">
                        <div className="flex gap-2.5 align-items-center">
                          <div className="">
                            <img
                              src={imgFile}
                              alt="Patient Signature"
                              style={{ maxHeight: '81px' }}
                            />
                            <span className="cross-icon flex-center" onClick={handleRemoveFile}>
                              <i className="icon-x-cros fs-14 lh-14 text-black"></i>
                            </span>
                          </div>
                        </div>
                      </div>
                    }
                  </>}
              </div>


              <div className="d-flex flex-column gap-6">
                <label className="custom_check_box d-flex gap-6 checkbox-multiline-alignment">
                  <input
                    type="checkbox"
                    className="form-check-input m-0"
                    checked={isConsentChecked}
                    onChange={(e) => setIsConsentChecked(e.target.checked)}
                  />
                  <p className="text text-black fs-14">I consent that my signature above is the same as a handwritten signature for the purposes of validity, enforceability, and admissibility. </p>
                </label>

                {validationErrors.ConsentCheckbox && (
                  <div className="text fs-14 Denied-clr">{validationErrors.ConsentCheckbox}</div>
                )}
              </div>

              <div className='ar_OTP-section d-flex flex-column gap-10'>
                <p className='Patient-Payment text fs-13 ar_text w-max-content'>We've sent a verification code to your email. Check the same email you used to open the intake form link.</p>
                <div className='px-2 '>
                  <p className='text font-m ar-light-color fs-13 pb-3px'>Enter OTP here</p>
                  <div className={`input mb-0 w-35 ${otpValidationError ? 'error' : ''}`}>
                    <input
                      type="text"
                      className="form-control h-28"
                      value={otpValue}
                      onChange={(e) => setOtpValue(e.target.value)}
                    />
                    {otpValidationError && (<>{otpValidationError}</>)}
                  </div>
                </div>


                <div className="d-flex justify-content-between align-items-center footer-btn px-10">
                  <button className="btn btn-orange h-auto submit-btn fs-14 font-sb" onClick={handleSubmit}>Submit Form</button>
                  <button className="btn btn-outline px-10 h-100 dark-clr" onClick={handleClearForm}>Clear Form</button>
                </div>

              </div>
            </div>


          </div>
        </div>
      }

      <SignEncounter
        isSign={isSign}
        setIsSign={setIsSign}
        providerName={formData?.PatientName || "Patient"}
        setSearchParam={() => { }}
        getSaveData={handleSignatureSave}
        isOpenedFrom={'PatientDefaultSignature'}
      />
    </div>
  )
}

export default PatientSigntureForm